import { zodResolver } from '@hookform/resolvers/zod';
import { Building2, Calendar, Globe, Plus, ShieldCheck, Users } from 'lucide-react';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { useCurrentUserCustomer, useUpdateCustomer } from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { UserManagement } from '@/features/shared/users';
import { CustomerDetailsFields } from '@/shared/components/customer/customer-details-fields';
import { CustomerQuotaCard } from '@/shared/components/customer/customer-quota-card';
import { DetailCard, DetailPageHeader, PageContainer } from '@/shared/components/layout';
import { Button, Form, FormLabel, Input } from '@/shared/components/ui';

const customerFormSchema = z.object({
  name: z.string().min(1),
  companyType: z.string().optional(),
  industry: z.string().optional(),
  website: z.string().url().optional().or(z.literal('')),
});

type CustomerFormValues = z.infer<typeof customerFormSchema>;

export function ManageCustomer() {
  const { t } = useIntl();
  const [usage, setUsage] = useState({ userCount: 0, inviteCount: 0 });
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const { data: customer } = useCurrentUserCustomer();
  const updateCustomer = useUpdateCustomer();
  const { user: currentUser } = useAuth();

  const form = useForm<CustomerFormValues, any, CustomerFormValues>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: {
      name: '',
      companyType: '',
      industry: '',
      website: '',
    },
  });

  useEffect(() => {
    if (customer) {
      form.reset({
        name: customer.name,
        companyType: customer.companyType || '',
        industry: customer.industry || '',
        website: customer.website || '',
      });
    }
  }, [customer, form]);

  const handleSubmit = async (data: CustomerFormValues) => {
    if (!customer || currentUser?.role !== 'customer-admin') return;
    try {
      await updateCustomer.mutateAsync({ id: customer.id, data });
      toast.success(t('customer.customerUpdatedSuccessfully'));
    } catch (_error) {
      form.setError('root', {
        type: 'manual',
        message: t('errors.unknownError'),
      });
    }
  };

  const canEditCustomer = currentUser?.role === 'customer-admin';
  const userQuota = customer?.userQuota ?? 0;
  const totalUsage = usage.userCount + usage.inviteCount;
  const handleUsageChange = useCallback((nextUsage: typeof usage) => {
    setUsage(nextUsage);
  }, []);

  const metaItems = customer
    ? [
        ...(customer.industry ? [{ icon: Building2, label: customer.industry }] : []),
        ...(customer.website
          ? [
              {
                icon: Globe,
                label: customer.website.replace('https://', ''),
                href: customer.website,
              },
            ]
          : []),
        ...(customer.createdAt
          ? [
              {
                icon: Calendar,
                label: `${t('common.since')} ${DateTime.fromISO(customer.createdAt).toFormat('dd.MM.yyyy')}`,
              },
            ]
          : []),
      ]
    : undefined;

  const avatarContent = customer ? (
    <span className="text-3xl font-bold">{customer.name.substring(0, 2).toUpperCase()}</span>
  ) : undefined;

  return (
    <PageContainer animation="slide-right">
      <DetailPageHeader
        title={customer?.name ?? t('customer.customer')}
        avatarContent={avatarContent}
        metaItems={metaItems}
        subtitle={!customer ? t('common.loading') : undefined}
        primaryAction={{
          label: updateCustomer.isPending ? t('common.saving') : t('common.save'),
          onClick: form.handleSubmit(handleSubmit),
          disabled: updateCustomer.isPending || !canEditCustomer,
          loading: updateCustomer.isPending,
        }}
      />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <DetailCard icon={Building2} title={t('customers.customerDetails')}>
                <CustomerDetailsFields
                  control={form.control}
                  disabled={!canEditCustomer}
                  labels={{
                    name: t('common.name'),
                    companyType: t('customer.companyType'),
                    industry: t('customer.industry'),
                    website: t('customer.website'),
                  }}
                  placeholders={{
                    name: t('customers.companyNamePlaceholder'),
                    companyType: t('customers.companyTypePlaceholder'),
                    industry: t('customers.industryPlaceholder'),
                    website: 'https://example.com',
                  }}
                  websiteWithIcon
                />
              </DetailCard>
            </div>

            <div className="space-y-6">
              <CustomerQuotaCard
                icon={ShieldCheck}
                title={t('customers.licenseAndQuota')}
                description={t('customer.userQuotaReadOnlyDescription')}
                usageLabel={t('customers.userLicenses')}
                totalUsage={totalUsage}
                quota={userQuota}
                usageDetails={t('customers.userQuotaUsageDetails', {
                  userCount: usage.userCount,
                  inviteCount: usage.inviteCount,
                })}
                quotaControl={
                  <div className="space-y-2">
                    <FormLabel>{t('customer.userQuotaLabel')}</FormLabel>
                    <Input value={userQuota || ''} readOnly disabled />
                  </div>
                }
              />
            </div>
          </div>
        </form>
      </Form>

      <DetailCard
        icon={Users}
        title={t('users.users')}
        noPadding
        className="mt-8"
        headerAction={
          canEditCustomer && (
            <Button variant="outline" size="sm" onClick={() => setIsInviteDialogOpen(true)}>
              <Plus className="size-4 mr-2" />
              {t('users.inviteUser')}
            </Button>
          )
        }
      >
        <UserManagement
          allowedRoles={['customer-admin']}
          hideInviteButton
          onUsageChange={handleUsageChange}
          embedded
          isInviteDialogOpen={isInviteDialogOpen}
          onInviteDialogOpenChange={setIsInviteDialogOpen}
        />
      </DetailCard>
    </PageContainer>
  );
}
